import Store from './Store'
import moment from 'moment'
import 'moment/locale/fr-ca'
import { flow, makeObservable, observable } from 'mobx'
import CalendarService from '../services/CalendarService'
import EventService from '../services/EventService'
import { DATE_FORMAT, SchedulerData, ViewTypes } from 'react-big-scheduler'
import Address from '../models/Address'
import Event from '../models/Event'

moment.locale('fr-ca')
moment.updateLocale('fr-ca', {
  week: {
    dow: 1, // First day of week is Monday
    doy: 6 // First week of year must contain 1 January (7 + 0 - 1)
  }
})

class CalendarStore extends Store {
  @observable dateStart = moment()

  /**
   *
   * @type {SchedulerData}
   */
  schedulerData = new SchedulerData(this.dateStart.format(DATE_FORMAT), ViewTypes.Week, false, false, {
    views: [
      { viewName: 'Jour', viewType: ViewTypes.Day, showAgenda: false, isEventPerspective: false },
      { viewName: 'Semaine', viewType: ViewTypes.Week, showAgenda: false, isEventPerspective: false },
      { viewName: 'Mois', viewType: ViewTypes.Month, showAgenda: false, isEventPerspective: false }
    ],
    startResizable: false,
    endResizable: false,
    movable: false,
    schedulerWidth: '95%',
    creatable: false,
    calendarPopoverEnabled: false,
    resourceName: 'Employés'
  },
  {
    isNonWorkingTimeFunc: (schedulerData, time) => {
      const { localeMoment } = schedulerData
      if (schedulerData.viewType === ViewTypes.Day) {
        const hour = localeMoment(time).hour()
        if (hour < 6 || hour > 18) return true
      } else {
        const dayOfWeek = localeMoment(time).weekday()
        if (dayOfWeek === 5 || dayOfWeek === 6) return true
      }

      return false
    }
  }, moment)

  constructor () {
    super()
    makeObservable(this)
  }

  @flow
  * setDate (date) {
    this.schedulerData.setDate(date.format(DATE_FORMAT))
    yield this.load()
  }

  @flow
  * confirmEvent (eventId) {
    const response = yield EventService.confirmEvent(eventId)
    if (!response.result.success) {
      throw Error(response.result.message)
    }
    yield this.load()
  }

  @flow
  * deleteEvent (eventId) {
    const response = yield EventService.deleteEvent(eventId)
    if (!response.result.success) {
      throw Error(response.result.message)
    }
    yield this.load()
  }

  @flow
  * nextDate () {
    this.schedulerData.next()
    yield this.load()
  }

  @flow
  * prevDate () {
    this.schedulerData.prev()
    yield this.load()
  }

  @flow
  * load () {
    this.isLoading = true
    const response = yield CalendarService.getEvents(
      moment(this.schedulerData.startDate).format('YYYY-MM-DD 00:00:00'),
      moment(this.schedulerData.endDate).format('YYYY-MM-DD 23:59:59'))
    console.log(response)
    this._createEvents(Object.values(response.events))
    this.isLoading = false
  }

  @flow
  * loadWithUsers (userIds) {
    this.isLoading = true
    const response = yield CalendarService.getUsersEvents(userIds,
      moment(this.schedulerData.startDate).format('YYYY-MM-DD 00:00:00'),
      moment(this.schedulerData.endDate).format('YYYY-MM-DD 23:59:59'))
    this._createEvents(Object.values(response.events))
    this.isLoading = false
  }

  _createEvents (_events) {
    const employees = {}
    const events = []
    for (const event of _events) {
      if (!employees[event.user.id]) {
        employees[event.user.id] = {
          id: event.user.id,
          name: `${event.user.first_name} ${event.user.last_name}`
        }
      }
      const colors = this._getColorFromStatus(event.status)
      const address = new Address()
      address.setOriginalData(event.clients_addresses_equipment.clients_address.address)
      events.push({
        id: event.id,
        start: event.date_start,
        end: event.date_end,
        resourceId: event.user.id,
        title: event.client.name,
        forms: JSON.parse(event.clients_addresses_equipment.forms),
        address,
        equipment: event.clients_addresses_equipment.equipment.name,
        eventObject: new Event(event),
        bgColor: colors.color,
        ...(colors.textColor
          ? {
              color: colors.textColor
            }
          : {}),
        resizable: false
      })
    }
    this.schedulerData.setEvents(events)
    this.schedulerData.setResources(Object.values(employees))
  }

  /**
   *
   * @param status
   * @returns {{}}
   */
  _getColorFromStatus (status) {
    const item = {}
    switch (status) {
      case 'CREATED':
        item.color = '#2d62a7'
        item.textColor = '#FFFFFF'
        break
      case 'STARTED':
        item.color = '#bb6960'
        break
      case 'WAITING_EVALUATION':
        item.color = '#c4a30b'
        break
      case 'COMPLETED':
        item.color = '#83f682'
        break
      case 'EXPIRED':
        item.textColor = '#FFFFFF'
        item.color = '#BB3333'
        break
      default:
        item.color = '#dddddd'
        break
    }
    return item
  }
}

export default CalendarStore
